import cn from 'classnames';
import { LoaderButton } from 'components/Button';
import { reloadCart } from 'components/Cart/actions';
import connect from 'components/Shared/connect';
import { RenderCheckbox, RenderLabeledInput } from 'components/Shared/Form/inputs';
import { isMobileLayout } from 'components/Shared/responsive';
import { AddressInfo } from 'models/AddressInfo';
import { CustomerType } from 'models/CustomerType';
import { ChangeEvent } from 'react';
import { EventWithDataHandler, Field, Fields, resetSection } from 'redux-form';
import { postGetAddressInfo } from 'services/ApiCheckoutPageService';
import batchAction from 'store/batching';
import { NO_ELEMENT, rm } from 'utils';
import {
  deliveryAddressChanged,
  onBlurCheckout,
  setAddressDetails
} from 'views/CheckoutPage/actions';
import { RenderAddress } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/AutoForm/renderAddress';
import { CertificateForm } from 'views/CheckoutPage/ContactInfo/themes/BGH-RD3/certificateForm';
import ContactInfoPolicy from 'views/CheckoutPage/PolicyInformation/themes/BGH-RD3/contactInfoPolicy';
import {
  checkoutForm,
  getAddressInfo,
  selectDisableMessageBoxFeatureToggle,
  getIsChannelPolarPumpen,
  getShouldUseRotFeatureToggle
} from 'views/CheckoutPage/selector';
import * as rules from 'views/CheckoutPage/validation';
import autoForm, {
  AddressFormProps,
  AutoFormMergedProps,
  AutoFormOwnProps,
  QuickStartFormProps
} from '../AutoForm';
import { LeaveMessage } from '../leaveMessage';
import commonStyles from '../styles.scss';
import RotForm from './rotForm';
import { RentalForm } from '../rentalForm';

const MIN_ID_LENGTH = 10;

interface QuickStartFormMergedprops extends QuickStartFormProps {
  isMobile: boolean;
  form: AddressInfo;
}

const QuickStartForm = connect<QuickStartFormMergedprops, QuickStartFormProps>(state => ({
  isMobile: isMobileLayout(state),
  form: getAddressInfo(state)
}))(({
  onChange,
  inputHandler,
  noAddressError,
  isMobile,
  loading,
  handleSubmit,
  submitDisabled,
  form
}: QuickStartFormMergedprops) => {
  const hasFetchedAddressWithIdentityNumber =
    !!form.identityNumber &&
    !!form.firstName &&
    !!form.lastName &&
    !!form.city &&
    !!form.street &&
    !!form.zipCode;

  if (hasFetchedAddressWithIdentityNumber) {
    return NO_ELEMENT;
  }

  const skipValidate = !form.identityNumber || form.identityNumber?.length < MIN_ID_LENGTH;

  return (
    <div className={commonStyles.quickStartForm}>
      <Field
        name="identityNumber"
        inputMode="numeric"
        pattern={isMobile ? '[0-9]*' : rules.identityNumberRegexString}
        className={commonStyles.identityNumber}
        validate={[rules.identityNumber, () => noAddressError]}
        component={RenderLabeledInput}
        props={{
          customLabel: rm('checkout_identityheader_private'),
          hideError: skipValidate
        }}
        onChange={onChange}
        onKeyPress={inputHandler}
      />
      <LoaderButton
        appearance="quaternary"
        loading={loading}
        className={commonStyles.getAddressButton}
        disabled={submitDisabled}
        onClick={handleSubmit}
        type="button"
      >
        {rm('checkout_identity_button')}
      </LoaderButton>
    </div>
  );
});

interface MergedProps extends AddressFormProps {
  hasRotDeduction: boolean;
  shouldDisableCardPaymentROTFeatureToggle: boolean;
  shouldDisableMessageBoxFeatureToggle: boolean;
  isChannelPP: boolean;
  deliveryAddressChanged: EventWithDataHandler<ChangeEvent>;
  onBlur: EventWithDataHandler<ChangeEvent>;
}

const AddressForm = connect<MergedProps>(
  state => ({
    hasRotDeduction: state.cart.hasRotDeduction,
    shouldDisableCardPaymentROTFeatureToggle: getShouldUseRotFeatureToggle(state),
    shouldDisableMessageBoxFeatureToggle: selectDisableMessageBoxFeatureToggle(state),
    isChannelPP: getIsChannelPolarPumpen(state)
  }),
  dispatch => ({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onBlur: (_e: ChangeEvent, n: any, _p: any, name: string) => dispatch(onBlurCheckout(name, n)),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    deliveryAddressChanged: (_e: ChangeEvent, n: any, _p: any, name: string) =>
      dispatch(deliveryAddressChanged(n, name))
  })
)(
  ({
    hasRotDeduction,
    shouldDisableCardPaymentROTFeatureToggle,
    shouldDisableMessageBoxFeatureToggle,
    isChannelPP,
    deliveryAddressChanged,
    onBlur,
    resetForm
  }: MergedProps) => (
    <>
      <div className={commonStyles.quickStartFormTop}>
        <div className={commonStyles.address}>
          <Fields
            names={[
              'identityNumber',
              'firstName',
              'lastName',
              'street',
              'city',
              'zipCode',
              'county'
            ]}
            label={rm('checkout_address')}
            component={RenderAddress(resetForm)}
          />
        </div>
        <div className={commonStyles.policy}>
          <ContactInfoPolicy desktop marginBottom />
        </div>

        {shouldDisableCardPaymentROTFeatureToggle && !isChannelPP ? (
          NO_ELEMENT
        ) : (
          <div className={commonStyles.altAddress}>
            <Field
              name="hasDeliveryAddress"
              onChange={deliveryAddressChanged}
              component={RenderCheckbox}
              label={rm('checkout_delivery_alternative_label')}
              className={commonStyles.checkboxWrapper}
            />
          </div>
        )}
      </div>

      <Field
        name="phoneNumber"
        type="tel"
        validate={rules.phone}
        className={cn(commonStyles.input, commonStyles.left)}
        component={RenderLabeledInput}
        onBlur={onBlur}
        autoFocus
        props={{ customLabel: rm('checkout_cellphone') }}
        inputMode="tel"
      />
      <Field
        name="email"
        type="email"
        validate={rules.email}
        className={cn(commonStyles.input, commonStyles.right)}
        component={RenderLabeledInput}
        onBlur={onBlur}
        props={{ customLabel: rm('checkout_email_rd3') }}
      />
      {shouldDisableMessageBoxFeatureToggle ? (
        NO_ELEMENT
      ) : (
        <LeaveMessage className={commonStyles.message} onBlur={onBlur} />
      )}
      {hasRotDeduction && <RotForm isManual={false} onBlur={onBlur} />}
      <RentalForm isManual={false} onBlur={onBlur} />
      <CertificateForm />
      <ContactInfoPolicy mobile />
    </>
  )
);

interface StateProps {
  address: AddressInfo;
  isMobile: boolean;
}

export default connect<AutoFormMergedProps, AutoFormOwnProps, StateProps>(
  state => ({
    address: checkoutForm(state, 'address') as AddressInfo,
    isMobile: isMobileLayout(state)
  }),
  dispatch => ({
    setAddressDetails: (args: AddressInfo) =>
      dispatch(batchAction(setAddressDetails(args), reloadCart())),
    reset: () => dispatch(resetSection('checkout', 'address.deliveryAddress'))
  }),
  ({ address, isMobile, ...state }, { loadDetails, setAddressDetails, ...dispatch }, ownProps) => ({
    ...state,
    ...dispatch,
    ...ownProps,
    isValid: address && rules.isValidIdentityNumber(address.identityNumber),
    showAddressForm: Boolean(address && address.street),
    hasDeliveryAddress: Boolean(address && address.hasDeliveryAddress),
    loadDetails: () =>
      postGetAddressInfo({
        customerType: CustomerType.Private,
        number: address.identityNumber
      }),
    setAddressDetails: (args: AddressInfo) =>
      setAddressDetails({
        ...args,
        identityNumber: address && address.identityNumber
      })
  })
)(autoForm(AddressForm, QuickStartForm));
